import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { TransparentButton } from '../../../../../../../../aida/components/TransparentButton';
import { Pill } from '../../../../../../../../web/components-v2/Pill/Pill';
import { Checkbox } from '../../../../../../../../web/components/Checkbox';
import { TrashIcon } from '../../../../../../../../web/components/svgs/icons/TrashIcon';
import { IRecipient } from '../../../../../../../models/Api';
import { styleSheet } from '../styles';
import { ErrorMessage } from './ErrorMessage';

interface GuestFieldsProps {
	guestLimit: number;
	requireGuestNames?: boolean;
	isPaidEvent?: boolean;
	hasPaidGuests?: boolean;
	guestCount?: number;
	DEFAULT_VALUES: { guests: IRecipient[] };
}

export const GuestFields: React.FC<GuestFieldsProps> = ({
	guestLimit,
	requireGuestNames = false,
	isPaidEvent = false,
	hasPaidGuests = false,
	guestCount = 0,
	DEFAULT_VALUES,
}) => {
	const {
		control,
		register,
		formState: { isSubmitted, errors },
		watch,
		setValue,
	} = useFormContext();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'guests',
	});

	const formValues = watch();

	return (
		<div className={css(styleSheet.plusOneOptionWrap)}>
			<div className={css(styleSheet.formControl)}>
				<Controller
					name='bringingGuest'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<Checkbox
							checked={fieldProps.value}
							className={css(styleSheet.checkbox)}
							id='bringingGuest'
							name='bringingGuest'
							disabled={isPaidEvent && hasPaidGuests && Boolean(guestCount)}
							onChange={() =>
								fieldProps.onChange(() => {
									const toggle = !fieldProps.value;
									if (!toggle) {
										setValue('guests', DEFAULT_VALUES.guests);
									}
									setValue('bringingGuest', toggle);
								})
							}
						>
							<div className={css(styleSheet.bringingGuestsLabel)}>I&apos;ll be bringing a guest</div>
						</Checkbox>
					)}
				/>
			</div>
			{formValues.bringingGuest ? (
				<>
					{fields.map((item, index) => {
						// Check if this guest is paid based on the isPaid flag
						const currentGuest = formValues.guests[index];
						const isGuestPaid = currentGuest?.isPaid || false;

						// For paid events, disable editing and removal of paid guests
						const isDisabled = isPaidEvent && isGuestPaid;

						return (
							<React.Fragment key={item.id}>
								<div className={css(styleSheet.guestInfo, isDisabled && styleSheet.disabledGuestInfo)}>
									<div className={css(styleSheet.formControl)}>
										<label htmlFor={`client-guest-${item.id}-first-name`} className={css(styleSheet.label)}>
											Guest First Name
											{requireGuestNames ? <span className={css(styleSheet.requiredAsterisk)}> *</span> : ':'}
										</label>
										<input
											id={`client-guest-${item.id}-first-name`}
											key={item.id}
											className={`text-input ${css(styleSheet.textInput)}`}
											{...register(`guests.${index}.firstName`)}
											type='text'
											placeholder={requireGuestNames || formValues.guests[index].lastName ? '' : 'Optional'}
											disabled={isDisabled}
										/>
										{isSubmitted && (errors.guests as any)?.[index]?.firstName && (
											<div className={css(styleSheet.guestInfoError)}>
												<ErrorMessage message={(errors.guests as any)[index]?.firstName?.message || ''} />
											</div>
										)}
									</div>
									<div className={css(styleSheet.formControl)}>
										<label htmlFor={`client-guest-${item.id}-last-name`} className={css(styleSheet.label)}>
											Guest Last Name
											{requireGuestNames ? <span className={css(styleSheet.requiredAsterisk)}> *</span> : ':'}
										</label>
										<input
											{...register(`guests.${index}.lastName`)}
											type='text'
											id={`client-guest-${item.id}-last-name`}
											key={item.id}
											className={`text-input ${css(styleSheet.textInput)}`}
											placeholder={requireGuestNames || formValues.guests[index].firstName ? '' : 'Optional'}
											disabled={isDisabled}
										/>
										{isSubmitted && (errors.guests as any)?.[index]?.lastName && (
											<div className={css(styleSheet.guestInfoError)}>
												<ErrorMessage message={(errors.guests as any)[index]?.lastName?.message || ''} />
											</div>
										)}
									</div>
									{isGuestPaid && <Pill label={`(Paid)`} size='small' variant='label' color={'green'} />}
									{!isDisabled ? (
										<TransparentButton
											type='button'
											onClick={() => {
												remove(index);
												if (!index && formValues.guests.length === 1) {
													// If the last guest is removed, reset the bringingGuest and guests fields
													setValue('bringingGuest', false);
													setValue('guests', DEFAULT_VALUES.guests);
												}
											}}
										>
											<TrashIcon className={css(styleSheet.removeGuestIcon)} />
										</TransparentButton>
									) : null}
								</div>
								{isSubmitted && (errors.guests as any)?.[index]?.message && (
									<div className={css(styleSheet.guestInfoError)}>
										<ErrorMessage message={(errors.guests as any)[index].message} />
									</div>
								)}
							</React.Fragment>
						);
					})}
					{guestLimit > fields.length ? (
						<button
							type='button'
							className={css(styleSheet.addAnotherGuestButton)}
							onClick={() => append({ firstName: '', lastName: '', isPaid: false })}
						>
							+ Add another guest
						</button>
					) : null}
					{isSubmitted && errors.guests?.message && (
						<div className={css(styleSheet.guestInfoError)}>
							<ErrorMessage message={errors.guests.message} />
						</div>
					)}
				</>
			) : null}
		</div>
	);
};
